<template>
  <div class="appointment-container">
    <v-breadcrumbs :items="items"></v-breadcrumbs>
    <v-form @submit.prevent="appointmentSave()" ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="appointment-card">
            <v-card-title class="text-h6">Patient Information</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="info-field">
                    <div class="info-label">Patient ID</div>
                    <div class="info-value">{{ patient.patient_id }}</div>
                  </div>

                  <div class="info-field">
                    <div class="info-label">Mobile Number</div>
                    <div class="info-value">{{ patient.patient_phone }}</div>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="info-field">
                    <div class="info-label">Patient Name</div>
                    <div class="info-value">{{ patient.patient_name }}</div>
                  </div>

                  <div class="info-field">
                    <div class="info-label">Gender</div>
                    <div class="info-value">{{ patient.patient_sex }}</div>
                  </div>
                </v-col>
                <v-col v-if="patient.note" cols="12">
                  <v-chip color="red" text-color="white">{{ patient.note }}</v-chip>
                </v-col>
                <v-divider class="my-4"></v-divider>

                <!-- Editable Fields -->
                <v-col cols="12">
                  <v-autocomplete
                      v-model="submit_data.doctor_id"
                      item-text="dr_name"
                      item-value="id"
                      :items="doctors"
                      label="Select Doctor"
                      outlined
                      dense
                      v-on:change="getDoctorTimeSlots()"
                      :loading="loadingStatus"
                      :disabled="dr_select_disabled === true"
                      hide-details
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-menu
                        v-model="menu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="submit_data.appointment_date"
                            label="Select Date"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                            class="flex-grow-0 mr-2"
                            style="width: 200px"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="submit_data.appointment_date"
                          @input="menu = false"
                          v-on:change="getDoctorTimeSlots()"
                          :min="nowDate"
                      ></v-date-picker>
                    </v-menu>

                    <v-btn
                        small
                        color="primary"
                        class="mr-2"
                        @click="changeDate('next')"
                        :title="'Next Day'"
                    >
                      Next <v-icon small>mdi-chevron-right</v-icon>
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        @click="changeDate('next_week')"
                        :title="'Same Day Next Week'"
                    >
                      Next Week
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                      label="Appointment Note"
                      prepend-inner-icon="mdi-comment"
                      rows="2"
                      v-model="submit_data.appointment_note"
                      outlined
                      hide-details
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-row align="center" justify="space-around" class="mb-2">
                <div v-if="spinnerButtons">
                  <v-progress-circular :width="12" size="61" color="primary" indeterminate></v-progress-circular>
                </div>
                <div class="text-center" v-else>
                  <v-btn class="mr-2" depressed color="primary" type="submit" @click="save('save')">
                    Save
                  </v-btn>
                  <v-btn depressed color="success" type="submit" @click="save('save_and_create_invoice')">
                    Save & Create Invoice
                  </v-btn>
                </div>
              </v-row>
            </v-card-actions>
          </v-card>

          <v-card class="mt-4">
            <v-card-title class="text-h6">Active Appointments</v-card-title>
            <v-card-text>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th class="text-left">Date</th>
                    <th class="text-left">Time</th>
                    <th class="text-left">Doctor</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(appointment, index) in active_appointments" :key="index">
                    <td>{{ appointment.appointment_date }}</td>
                    <td>{{ appointment.time_slot }}</td>
                    <td>{{ appointment.doctor.dr_name }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <!-- Appointment Success Card -->
          <v-card v-if="showSuccessCard" class="mb-4 success-card">
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div class="text-h6 success-title">
                  <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
                  Appointment Confirmed
                </div>
                <v-btn icon small @click="showSuccessCard = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="appointment-details">
                <div class="detail-row">
                  <span class="detail-label">Patient:</span>
                  <span class="detail-value">{{ patient.patient_name }} ({{ patient.patient_id }})</span>
                </div>
                <!-- <div class="detail-row">
                  <span class="detail-label">Doctor:</span>
                  <span class="detail-value">{{ selectedDoctorName }}</span>
                </div> -->
                <div class="detail-row">
                  <span class="detail-label">Date:</span>
                  <span class="detail-value">{{ formattedDate }}</span>
                </div>
                <div class="detail-row" v-if="savedAppointment">
                  <span class="detail-label">Time:</span>
                  <span class="detail-value">

                    {{ savedAppointment.time_slot }} (Serial: {{ savedAppointment.serial_no }})
                  </span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Existing Doctor Schedule Card -->
          <v-card v-if="selectedDoctorName && formattedDate" class="title-card mb-4">
            <v-card-text>
              <div class="appointment-title">
                Appointment time slots for
                <span class="highlight-text">{{ selectedDoctorName }}</span> on
                <span class="highlight-text">{{ formattedDate }}</span>
              </div>
              <div class="slot-stats mt-2">
                <v-chip color="success" small>Available: {{ availableSlots }}</v-chip>
                <v-chip color="error" small class="ml-2">Booked: {{ bookedSlots }}</v-chip>
              </div>
            </v-card-text>
          </v-card>
          <v-card v-for="(group, index) in time_slots" :key="index" class="mb-4">

            <v-row>
              <v-col cols="12" md="2">
                <v-card outlined class="group_hours" style="height: 60px">
                  <v-card-title>{{ group.hour }} </v-card-title>
                </v-card>
              </v-col>

              <v-col
                  v-for="(slot, idx) in group.slots"
                  :key="idx"
                  cols="3" md="2" sm="3"
                  class="text-center mt-1"
              >
                <v-card outlined :class="{ 'red--text': slot.booked }" @click="selectSlot(slot)" style="height: 60px">
                  <v-card-text>
                    <v-radio-group v-model="submit_data.time_slot" class="mt-n4 font-weight-bold">
                      <template v-if="!slot.booked">
                        <v-radio
                            :label="`${slot.serial}`"
                            :value="slot.time"
                            hide-details
                        ></v-radio>
                      </template>

                      <template v-else>
                        <div class="disabled-slot text-muted">
                          {{ slot.serial }}
                        </div>
                      </template>
                      <div class="time-label font-weight-bold ms-5">{{ slot.time }}</div>
                    </v-radio-group>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import atshq from "../../../js/setup";
import moment from "moment";
export default {
  data() {
    return {
      menu: false,
      valid: false,
      check_serial_no_available: true,
      nowDate: moment().format('YYYY-MM-DD'),
      loadingStatus: true,
      spinnerButtons: true,
      dr_select_disabled: false,
      headers: [
        {text: 'Date', value: 'appointment_date'},
        {text: 'Time', value: 'time_slot'},
        {text: 'Doctor', value: 'doctor.dr_name'},
      ],
      submit_data: {
        patient_id: this.$route.params.id,
        appointment_id: '',
        doctor_id: '',
        appointment_date: moment().format('YYYY-MM-DD'),
        waiting_status: 'Pending',
        serial_no: '',
        time_slot: '',
        appointment_note: '',
        create_save_type: '',
        user_id: localStorage.getItem("userId"),
      },
      create_save_type: '',
      doctors: [],
      patient: {},
      time_slots: [],
      active_appointments: [],
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Appointment Create",
          disabled: true,
          href: "/",
        },
      ],
      baseUrl: atshq.defaults.headers.common.base_URL,
      showSuccessCard: false,
      savedAppointment: null,
    };
  },
  created() {
    this.initialize();
    this.getFromParameters()
  },
  computed: {
    selectedDoctorName() {
      if (!this.submit_data.doctor_id || !this.doctors.length) return '';
      const doctor = this.doctors.find(d => d.id === this.submit_data.doctor_id);
      return doctor ? doctor.dr_name : '';
    },
    formattedDate() {
      return this.submit_data.appointment_date ?
          moment(this.submit_data.appointment_date).format('DD MMMM, YYYY') : '';
    },
    availableSlots() {
      return this.time_slots.reduce((total, group) => {
        return total + group.slots.filter(slot => !slot.booked).length;
      }, 0);
    },
    bookedSlots() {
      return this.time_slots.reduce((total, group) => {
        return total + group.slots.filter(slot => slot.booked).length;
      }, 0);
    }
  },
  methods: {
    getFromParameters() {
      if (this.$route.query.doctor_id && this.$route.query.appointment_id && this.$route.query.status === 'reschedule') {
        this.submit_data.doctor_id = parseInt(this.$route.query.doctor_id)
        this.submit_data.appointment_id = this.$route.query.appointment_id
        this.dr_select_disabled = true
        this.getDoctorTimeSlots()
      }
    },
    initialize() {
      atshq
          .get("appointments/appointments/create?patient_id=" + this.submit_data.patient_id)
          .then((response) => {
            this.patient = response.data.patient;
            this.active_appointments = response.data.active_appointments
            this.doctors = response.data.doctors
            this.loadingStatus = false;
            this.spinnerButtons = false
          })
          .catch((e) => {
            this.$noty.error(e.response.data.message);
          });
    },
    appointmentSave() {
      if (!this.submit_data.doctor_id){
        this.$noty.error("Doctor name is required");
        return;
      }

      if (!this.submit_data.appointment_date){
        this.$noty.error("Appointment date is required");
        return;
      }

      if (this.check_serial_no_available && !this.submit_data.serial_no) {
        this.$noty.error("Please select serial no");
        return;
      }
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'success',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, submit it!'
      }).then((result) => {
        if (result.value) {
          this.spinnerButtons = true;
          let submitUrl =  "appointments/appointments";
          atshq
              .post(submitUrl, {
                submit_data: this.submit_data,
              })
              .then((response) => {
                this.loadingStatus = false;
                this.spinnerButtons = false;
                this.savedAppointment = response.data.appointment;
                this.showSuccessCard = true;
                this.time_slots = [];
                this.submit_data.doctor_id = '';
                this.submit_data.serial_no = '';
                this.submit_data.time_slot = '';
                this.$noty.success(response.data.message);
                if((this.create_save_type == 'save_and_create_invoice') &&  response.data.url) {
                  window.location.href = response.data.url
                }
                //Start: After reschedule appointment done, then all url parameters remove
                this.$router.replace({
                  path: this.$route.path,
                  query: {} // Empty object to clear the query
                }).catch(() => {});
                //End: After reschedule appointment done, then all url parameters remove
              })
              .catch((e) => {
                this.$noty.error(e.response.data.message);
                this.loadingStatus = false;
                this.spinnerButtons = false
              });
        }
      });
    },
    selectSlot(slot) {
      if (!slot.booked) {
        this.submit_data.time_slot = slot.time;
        this.submit_data.serial_no = slot.serial;
      }
    },
    save(type) {
      if(type == 'save') {
        this.create_save_type = 'save'
      } else if(type == 'save_and_create_invoice') {
        this.create_save_type = 'save_and_create_invoice'
      }
    },
    getDoctorTimeSlots() {
      this.spinnerButtons = true
      this.time_slots = [];
      this.submit_data.serial_no = '';
      this.submit_data.time_slot = '';
      if(this.submit_data.doctor_id && this.submit_data.appointment_date){
        atshq
            .get('appointments/get-doctor-time-slots/'+this.submit_data.doctor_id+'/'+this.submit_data.appointment_date+'/'+this.submit_data.patient_id)
            .then((response) => {
              this.time_slots = response.data.time_slots
              this.check_serial_no_available = response.data.check_serial_no_available
              if (!this.check_serial_no_available){
                this.submit_data.serial_no = 0;
              }
              this.spinnerButtons = false
            })
            .catch((e) => {
              this.$noty.error(e.response.data.message);
              this.spinnerButtons = false
            });
      }
    },
    changeDate(type) {
      const currentDate = moment(this.submit_data.appointment_date);

      switch(type) {
        case 'next':
          this.submit_data.appointment_date = currentDate.add(1, 'days').format('YYYY-MM-DD');
          break;
        case 'next_week':
          this.submit_data.appointment_date = currentDate.add(7, 'days').format('YYYY-MM-DD');
          break;
      }

      this.getDoctorTimeSlots();
    },
  },
  watch: {
    showSuccessCard(val) {
      if (!val) {
        this.savedAppointment = null;
      }
    }
  }
};
</script>

<style scoped>
.appointment-container {
  padding: 16px;
  background-color: whitesmoke;
  min-height: calc(100vh - 64px);
}

.appointment-card, .time-slots-card {
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.appointment-card:hover, .time-slots-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.text-center {
  text-align: center;
}

.red--text {
  color: rgba(255, 255, 255, 0.11);
  background-color: red;
}

.mt-4 {
  margin-top: 16px;
}

.time-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}

.disabled-slot {
  opacity: 0.5;
  cursor: not-allowed;
}

.info-field {
  padding: 8px;
  border-radius: 4px;
  background-color: #f5f5f5;
  margin-bottom: 8px;
}

.info-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.info-value {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}

.my-4 {
  margin-top: 16px;
  margin-bottom: 16px;
}

.appointment-title {
  margin-bottom: 16px;
  color: var(--v-primary-base);
  font-size: 1.5rem;
}

.title-card {
  background-color: #f8f9fa;
  border-left: 4px solid var(--v-primary-base);
}

.highlight-text {
  background-color: #e3f2fd;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.slot-stats {
  font-size: 0.875rem;
}
.group_hours {
  background-color: yellowgreen;
  color: white;
  font-weight: bold;
  font-size: 18px;
}

.success-card {
  background-color: #f1f8e9;
  border-left: 4px solid #4caf50;
}

.success-title {
  color: #2e7d32;
}

.appointment-details {
  margin-top: 16px;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
  align-items: center;
}

.detail-label {
  min-width: 100px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.detail-value {
  color: rgba(0, 0, 0, 0.87);
}
</style>
